import { type FormEvent, useCallback, useEffect, useState } from 'react';
import QRCode from 'qrcode';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AdminSwapPoolsPanel, AdminSwapTokensPanel } from '../components/AdminSwapCatalogPanels';
import { LanguageSelect } from '../components/LanguageSelect';
import { getJSON, postJSON, postLogout, type LoginResponse, type MessageResponse, type UsersResponse, type UserRow } from '../lib/api';
import { sha256Utf8Hex } from '../lib/passwordWire';
import { adminMainClass, pageWrapClass } from '../layout/page';
import {
  inputStackFieldClass,
  outlineButtonInlineClass,
  outlineSoftButtonClass,
  primaryButtonBlockClass,
} from '../components/form/fieldClasses';

type View = 'login' | 'change' | 'panel';
type AdminTab = 'users' | 'tokens' | 'pools';

export default function AdminPage() {
  const { t, i18n } = useTranslation();
  const [view, setView] = useState<View>('login');
  const [adminPassword, setAdminPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newUserName, setNewUserName] = useState('');
  const [newUserPassword, setNewUserPassword] = useState('');
  const [rows, setRows] = useState<UserRow[]>([]);
  const [msg, setMsg] = useState('');
  const [msgOk, setMsgOk] = useState(false);
  const [busy, setBusy] = useState(false);
  const [pwdReset, setPwdReset] = useState<{ id: number; username: string } | null>(null);
  const [pwdResetValue, setPwdResetValue] = useState('');
  const [mfaUser, setMfaUser] = useState<UserRow | null>(null);
  const [mfaOtpauthUrl, setMfaOtpauthUrl] = useState('');
  const [mfaQrDataUrl, setMfaQrDataUrl] = useState('');
  const [mfaVerifyCode, setMfaVerifyCode] = useState('');
  const [adminTab, setAdminTab] = useState<AdminTab>('users');

  const resetMessage = useCallback(() => {
    setMsg('');
    setMsgOk(false);
  }, []);

  const loadUsers = useCallback(async () => {
    const { res, data } = await getJSON<UsersResponse>('/api/admin/users');
    if (!res.ok) {
      setMsgOk(false);
      setMsg(data.error || t('common.error'));
      return;
    }
    setRows(data.users ?? []);
  }, [t]);

  useEffect(() => {
    if (view === 'panel' && adminTab === 'users') {
      void loadUsers();
    }
  }, [view, adminTab, loadUsers, i18n.language]);

  useEffect(() => {
    if (!mfaOtpauthUrl) {
      setMfaQrDataUrl('');
      return;
    }
    let cancelled = false;
    void QRCode.toDataURL(mfaOtpauthUrl, { width: 220, margin: 2 })
      .then((url) => {
        if (!cancelled) {
          setMfaQrDataUrl(url);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setMfaQrDataUrl('');
        }
      });
    return () => {
      cancelled = true;
    };
  }, [mfaOtpauthUrl]);

  async function onLogin(e: FormEvent) {
    e.preventDefault();
    resetMessage();
    setBusy(true);
    try {
      const { res, data } = await postJSON<LoginResponse>('/api/admin/login', {
        password: await sha256Utf8Hex(adminPassword),
      });
      if (!res.ok) {
        setMsg(data.error || t('common.error'));
        return;
      }
      if (data.must_change_password) {
        setView('change');
        return;
      }
      setView('panel');
    } finally {
      setBusy(false);
    }
  }

  async function onChangePassword(e: FormEvent) {
    e.preventDefault();
    resetMessage();
    setBusy(true);
    try {
      const { res, data } = await postJSON<MessageResponse>('/api/admin/change-password', {
        new_password: await sha256Utf8Hex(newPassword),
      });
      if (!res.ok) {
        setMsg(data.error || t('common.error'));
        return;
      }
      setMsgOk(true);
      setMsg(data.message || '');
      setView('panel');
      setNewPassword('');
    } finally {
      setBusy(false);
    }
  }

  async function onCreateUser(e: FormEvent) {
    e.preventDefault();
    resetMessage();
    setBusy(true);
    try {
      const { res, data } = await postJSON<MessageResponse>('/api/admin/users', {
        username: newUserName.trim(),
        password: await sha256Utf8Hex(newUserPassword),
      });
      if (!res.ok) {
        setMsg(data.error || t('common.error'));
        return;
      }
      setMsgOk(true);
      setMsg(data.message || '');
      setNewUserName('');
      setNewUserPassword('');
      await loadUsers();
    } finally {
      setBusy(false);
    }
  }

  async function onLogout() {
    await postLogout();
      setView('login');
      setAdminPassword('');
      setNewPassword('');
      setRows([]);
      setAdminTab('users');
      setPwdReset(null);
    setPwdResetValue('');
    setMfaUser(null);
    setMfaOtpauthUrl('');
    resetMessage();
  }

  async function toggleBan(u: UserRow) {
    if (u.is_admin) return;
    resetMessage();
    setBusy(true);
    try {
      const { res, data } = await postJSON<MessageResponse>('/api/admin/users/set-banned', {
        user_id: u.id,
        banned: !u.banned,
      });
      if (!res.ok) {
        setMsg(data.error || t('common.error'));
        return;
      }
      setMsgOk(true);
      setMsg(data.message || '');
      await loadUsers();
    } finally {
      setBusy(false);
    }
  }

  async function beginMfaSetup(u: UserRow) {
    if (u.is_admin) {
      return;
    }
    resetMessage();
    setBusy(true);
    try {
      const { res, data } = await postJSON<{ otpauth_url?: string; error?: string }>('/api/admin/users/mfa/begin', {
        user_id: u.id,
      });
      if (!res.ok) {
        setMsgOk(false);
        setMsg(data.error || t('common.error'));
        return;
      }
      setMfaUser(u);
      setMfaOtpauthUrl(data.otpauth_url ?? '');
      setMfaVerifyCode('');
    } finally {
      setBusy(false);
    }
  }

  async function confirmMfaVerify(e: FormEvent) {
    e.preventDefault();
    if (!mfaUser) {
      return;
    }
    resetMessage();
    setBusy(true);
    try {
      const { res, data } = await postJSON<MessageResponse>('/api/admin/users/mfa/verify', {
        user_id: mfaUser.id,
        code: mfaVerifyCode.trim(),
      });
      if (!res.ok) {
        setMsgOk(false);
        setMsg(data.error || t('common.error'));
        return;
      }
      setMsgOk(true);
      setMsg(data.message || '');
      setMfaUser(null);
      setMfaOtpauthUrl('');
      await loadUsers();
    } finally {
      setBusy(false);
    }
  }

  async function disableMfa(u: UserRow) {
    if (u.is_admin) {
      return;
    }
    resetMessage();
    setBusy(true);
    try {
      const { res, data } = await postJSON<MessageResponse>('/api/admin/users/mfa/disable', {
        user_id: u.id,
      });
      if (!res.ok) {
        setMsgOk(false);
        setMsg(data.error || t('common.error'));
        return;
      }
      setMsgOk(true);
      setMsg(data.message || '');
      await loadUsers();
    } finally {
      setBusy(false);
    }
  }

  async function submitPwdReset(e: FormEvent) {
    e.preventDefault();
    if (!pwdReset) return;
    resetMessage();
    setBusy(true);
    try {
      const { res, data } = await postJSON<MessageResponse>('/api/admin/users/reset-password', {
        user_id: pwdReset.id,
        new_password: await sha256Utf8Hex(pwdResetValue.trim()),
      });
      if (!res.ok) {
        setMsg(data.error || t('common.error'));
        return;
      }
      setMsgOk(true);
      setMsg(data.message || '');
      setPwdReset(null);
      setPwdResetValue('');
      await loadUsers();
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className={pageWrapClass}>
      <main className={adminMainClass}>
        <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
          <h1 className="text-xl font-semibold tracking-tight text-slate-900">
            {view === 'login' ? t('admin.loginTitle') : t('admin.title')}
          </h1>
          <LanguageSelect />
        </div>

        {view === 'login' && (
          <form className="grid max-w-md gap-3" onSubmit={(e) => void onLogin(e)}>
            <input
              required
              type="password"
              autoComplete="current-password"
              className={inputStackFieldClass}
              placeholder={t('admin.adminPwd')}
              value={adminPassword}
              onChange={(e) => setAdminPassword(e.target.value)}
            />
            <button type="submit" disabled={busy} className={primaryButtonBlockClass}>
              {t('admin.login')}
            </button>
          </form>
        )}

        {view === 'change' && (
          <form className="grid max-w-md gap-3" onSubmit={(e) => void onChangePassword(e)}>
            <p className="text-sm text-slate-600">{t('admin.mustChange')}</p>
            <input
              required
              type="password"
              autoComplete="new-password"
              className={inputStackFieldClass}
              placeholder={t('admin.newPassword')}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
            />
            <button type="submit" disabled={busy} className={primaryButtonBlockClass}>
              {t('admin.updatePwd')}
            </button>
          </form>
        )}

        {view === 'panel' && (
          <div className="grid gap-6">
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 pb-4">
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  className={`h-9 rounded-lg px-3 text-sm font-medium ${
                    adminTab === 'users' ? 'bg-blue-600 text-white' : 'border border-slate-300 bg-white text-slate-800 hover:bg-slate-50'
                  }`}
                  onClick={() => setAdminTab('users')}
                >
                  {t('admin.tabUsers')}
                </button>
                <button
                  type="button"
                  className={`h-9 rounded-lg px-3 text-sm font-medium ${
                    adminTab === 'tokens' ? 'bg-blue-600 text-white' : 'border border-slate-300 bg-white text-slate-800 hover:bg-slate-50'
                  }`}
                  onClick={() => setAdminTab('tokens')}
                >
                  {t('admin.tabTokens')}
                </button>
                <button
                  type="button"
                  className={`h-9 rounded-lg px-3 text-sm font-medium ${
                    adminTab === 'pools' ? 'bg-blue-600 text-white' : 'border border-slate-300 bg-white text-slate-800 hover:bg-slate-50'
                  }`}
                  onClick={() => setAdminTab('pools')}
                >
                  {t('admin.tabPools')}
                </button>
              </div>
              <button type="button" className={outlineSoftButtonClass} onClick={() => void onLogout()}>
                {t('admin.logout')}
              </button>
            </div>

            {adminTab === 'tokens' ? (
              <AdminSwapTokensPanel
                t={t}
                busy={busy}
                setBusy={setBusy}
                setMsg={setMsg}
                setMsgOk={setMsgOk}
                resetMessage={resetMessage}
              />
            ) : null}

            {adminTab === 'pools' ? (
              <AdminSwapPoolsPanel
                t={t}
                busy={busy}
                setBusy={setBusy}
                setMsg={setMsg}
                setMsgOk={setMsgOk}
                resetMessage={resetMessage}
              />
            ) : null}

            {adminTab === 'users' ? (
              <>
            <h2 className="text-lg font-semibold text-slate-900">{t('admin.userList')}</h2>

            <form
              className="grid gap-3 rounded-xl border border-slate-200 bg-slate-50/80 p-4 md:grid-cols-[1fr_1fr_auto] md:items-end"
              onSubmit={(e) => void onCreateUser(e)}
            >
              <input
                required
                autoComplete="off"
                className={inputStackFieldClass}
                placeholder={t('admin.thUser')}
                value={newUserName}
                onChange={(e) => setNewUserName(e.target.value)}
              />
              <input
                required
                type="password"
                autoComplete="new-password"
                className={inputStackFieldClass}
                placeholder={t('admin.initialPwd')}
                value={newUserPassword}
                onChange={(e) => setNewUserPassword(e.target.value)}
              />
              <button type="submit" disabled={busy} className={primaryButtonBlockClass}>
                {t('admin.create')}
              </button>
            </form>

            {pwdReset && (
              <form
                className="grid gap-3 rounded-xl border border-amber-200 bg-amber-50/60 p-4 md:grid-cols-[1fr_auto_auto] md:items-end"
                onSubmit={(e) => void submitPwdReset(e)}
              >
                <div className="md:col-span-3 text-sm font-medium text-slate-800">
                  {t('admin.resetPwdTitle', { name: pwdReset.username })}
                </div>
                <input
                  required
                  type="password"
                  autoComplete="new-password"
                  className={inputStackFieldClass}
                  placeholder={t('admin.resetPwdHint')}
                  value={pwdResetValue}
                  onChange={(e) => setPwdResetValue(e.target.value)}
                />
                <button type="submit" disabled={busy} className={primaryButtonBlockClass}>
                  {t('admin.resetPwdSubmit')}
                </button>
                <button
                  type="button"
                  disabled={busy}
                  className={outlineButtonInlineClass}
                  onClick={() => {
                    setPwdReset(null);
                    setPwdResetValue('');
                  }}
                >
                  {t('admin.resetPwdCancel')}
                </button>
              </form>
            )}

            <div className="overflow-x-auto rounded-xl border border-slate-200">
              <table className="min-w-[52rem] w-full border-collapse text-left text-sm">
                <thead className="bg-slate-50 text-xs font-semibold uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="border-b border-slate-200 px-3 py-2">{t('admin.thId')}</th>
                    <th className="border-b border-slate-200 px-3 py-2">{t('admin.thUser')}</th>
                    <th className="border-b border-slate-200 px-3 py-2">{t('admin.thRole')}</th>
                    <th className="border-b border-slate-200 px-3 py-2">{t('admin.thMust')}</th>
                    <th className="border-b border-slate-200 px-3 py-2">{t('admin.thBanned')}</th>
                    <th className="border-b border-slate-200 px-3 py-2">{t('admin.thMfa')}</th>
                    <th className="border-b border-slate-200 px-3 py-2">{t('admin.thCreated')}</th>
                    <th className="border-b border-slate-200 px-3 py-2">{t('admin.actions')}</th>
                  </tr>
                </thead>
                <tbody className="text-slate-800">
                  {rows.map((u) => (
                    <tr key={u.id} className="odd:bg-white even:bg-slate-50/50">
                      <td className="border-b border-slate-100 px-3 py-2 tabular-nums">{u.id}</td>
                      <td className="border-b border-slate-100 px-3 py-2">{u.username}</td>
                      <td className="border-b border-slate-100 px-3 py-2">
                        {u.is_admin ? t('admin.roleAdmin') : t('admin.roleUser')}
                      </td>
                      <td className="border-b border-slate-100 px-3 py-2">
                        {u.must_change_password ? t('common.yes') : t('common.no')}
                      </td>
                      <td className="border-b border-slate-100 px-3 py-2">
                        {u.banned ? t('common.yes') : t('common.no')}
                      </td>
                      <td className="border-b border-slate-100 px-3 py-2">
                        {u.mfa_enabled ? t('common.yes') : t('common.no')}
                      </td>
                      <td className="border-b border-slate-100 px-3 py-2">{String(u.created_at)}</td>
                      <td className="border-b border-slate-100 px-3 py-2">
                        {u.is_admin ? (
                          <span className="text-slate-400">—</span>
                        ) : (
                          <div className="flex flex-wrap gap-2">
                            <button
                              type="button"
                              disabled={busy}
                              className="rounded-md border border-slate-300 bg-white px-2 py-1 text-xs font-medium text-slate-800 hover:bg-slate-50 disabled:opacity-50"
                              onClick={() => void toggleBan(u)}
                            >
                              {u.banned ? t('admin.unban') : t('admin.ban')}
                            </button>
                            <button
                              type="button"
                              disabled={busy}
                              className="rounded-md border border-slate-300 bg-white px-2 py-1 text-xs font-medium text-slate-800 hover:bg-slate-50 disabled:opacity-50"
                              onClick={() => {
                                resetMessage();
                                setPwdReset({ id: u.id, username: u.username });
                                setPwdResetValue('');
                              }}
                            >
                              {t('admin.resetPwd')}
                            </button>
                            {u.mfa_enabled ? (
                              <button
                                type="button"
                                disabled={busy}
                                className="rounded-md border border-amber-300 bg-amber-50 px-2 py-1 text-xs font-medium text-amber-900 hover:bg-amber-100 disabled:opacity-50"
                                onClick={() => void disableMfa(u)}
                              >
                                {t('admin.mfaRemove')}
                              </button>
                            ) : (
                              <button
                                type="button"
                                disabled={busy}
                                className="rounded-md border border-blue-300 bg-blue-50 px-2 py-1 text-xs font-medium text-blue-900 hover:bg-blue-100 disabled:opacity-50"
                                onClick={() => void beginMfaSetup(u)}
                              >
                                {t('admin.mfaSetup')}
                              </button>
                            )}
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {mfaUser ? (
              <div
                className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
                role="dialog"
                aria-modal
              >
                <div className="max-h-[90vh] w-full max-w-md overflow-y-auto rounded-2xl border border-slate-200 bg-white p-5 shadow-xl">
                  <h3 className="text-lg font-semibold text-slate-900">{t('admin.mfaModalTitle', { name: mfaUser.username })}</h3>
                  <p className="mt-2 text-sm text-slate-600">{t('admin.mfaModalHint')}</p>
                  {mfaQrDataUrl ? (
                    <div className="mt-4 flex justify-center">
                      <img src={mfaQrDataUrl} alt="" width={220} height={220} className="rounded-lg border border-slate-200" />
                    </div>
                  ) : (
                    <p className="mt-4 text-sm text-slate-500">{t('admin.mfaQrLoading')}</p>
                  )}
                  <form className="mt-4 grid gap-3" onSubmit={(e) => void confirmMfaVerify(e)}>
                    <input
                      required
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      className={inputStackFieldClass}
                      placeholder={t('admin.mfaVerifyPh')}
                      value={mfaVerifyCode}
                      onChange={(e) => setMfaVerifyCode(e.target.value)}
                    />
                    <div className="flex flex-wrap gap-2">
                      <button type="submit" disabled={busy} className={primaryButtonBlockClass}>
                        {t('admin.mfaConfirm')}
                      </button>
                      <button
                        type="button"
                        disabled={busy}
                        className={outlineButtonInlineClass}
                        onClick={() => {
                          setMfaUser(null);
                          setMfaOtpauthUrl('');
                          resetMessage();
                        }}
                      >
                        {t('admin.mfaCancel')}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            ) : null}
              </>
            ) : null}
          </div>
        )}

        {msg ? (
          <p className={`mt-4 min-h-5 text-sm ${msgOk ? 'text-emerald-700' : 'text-red-600'}`}>{msg}</p>
        ) : (
          <p className="mt-4 min-h-5 text-sm" />
        )}

        <p className="mt-4 text-sm">
          <Link className="font-medium text-blue-600 hover:text-blue-700" to="/">
            {t('admin.goUser')}
          </Link>
        </p>
      </main>
    </div>
  );
}
